import { Trans } from '@lingui/react';
import { useRouter } from 'next/router';
import * as React from 'react';
import { extractDoctor } from '../../../client/BffClient/extractors';
import { extractMfa } from '../../../client/BffClient/extractors/extractMfa';
import { routes } from '../../../routes';
import { Lock } from '../../atoms/Lock';
import { Mfa } from '../../atoms/Mfa';
import { NavItem } from '../../atoms/NavItem';
import { Skeleton } from '../../atoms/Skeleton';
import { BasePageTemplate } from '../../templates/BasePageTemplate';
import { ProfileBadge } from '../../atoms/Icon/icons/ProfileBadge';
import { Typography } from '../../atoms';
import { CircleIcon } from '../../atoms/CircleIcon';
import { useQuery } from '@tanstack/react-query';
import { convertIsoDate } from '../../../utils/convertIsoDate';
import { signOut } from 'next-auth/react';

export interface IIndexPageProps {
  getUser(): Promise<ReturnType<typeof extractDoctor>>;
  getMfa(): Promise<ReturnType<typeof extractMfa>>;
}

const IndexPage: React.FC<IIndexPageProps> = ({ getUser, getMfa }) => {
  const { push, query } = useRouter();
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const user = await getUser();
      if (user.logout) {
        await signOut().then(() => {
          window.location.href = `/api/auth/${query.businessUnit}/logged-out`;
        });
      }
      return user;
    },
  });
  const mfaQuery = useQuery({
    queryKey: ['mfa'],
    queryFn: getMfa,
  });

  const isPending =
    userQuery.isPending ||
    userQuery.isFetching ||
    mfaQuery.isPending ||
    mfaQuery.isFetching ||
    userQuery.data?.logout;
  const isLoading = userQuery.isPending || mfaQuery.isPending;
  const businessUnit = query.businessUnit as string;
  const lastPasswordReset = userQuery.data?.last_password_reset
    ? convertIsoDate(userQuery.data?.last_password_reset)
    : '';
  return (
    <BasePageTemplate
      page="index"
      doctor={{
        name: (
          <Skeleton active={userQuery.isPending || userQuery.data?.logout}>
            {userQuery?.data?.name || 'Username'}
          </Skeleton>
        ),
        title: (
          <Skeleton active={isLoading || userQuery.data?.logout}>
            {userQuery?.data?.name || 'Username'}
          </Skeleton>
        ),
      }}
      title={
        <Trans
          id="Welcome,<0></0>"
          components={{
            0: (
              <>
                &nbsp;
                <Skeleton active={isPending}>
                  {userQuery?.data?.name || 'Username'}
                </Skeleton>
              </>
            ),
          }}
        />
      }
      subtitle={
        <Trans id="Manage your privacy and security to make Align products work better for you." />
      }
    >
      {businessUnit === 'exocad' && (
        <>
          <Typography variant={'heading2'}>
            <Trans id="Login" />
          </Typography>
          <NavItem
            icon={<ProfileBadge />}
            title={<Trans id="Email" />}
            subtitle={
              <Skeleton active={isPending}>
                {userQuery.data?.email || 'Firstname Lastname'}
              </Skeleton>
            }
            onClick={() =>
              push({
                pathname: routes.email(),
                query: { businessUnit: query.businessUnit },
              })
            }
          />
        </>
      )}
      {(businessUnit === 'invisalign' || businessUnit === 'tps') && !(isLoading || userQuery.data?.isDsoLinkedStaff) && (
        <>
          <Typography variant={'heading2'}>
            <Trans id="Login" />
          </Typography>
          <NavItem
            icon={<ProfileBadge />}
            title={<Trans id="Username" />}
            subtitle={
              <Skeleton active={isPending}>
                {userQuery.data?.username || 'Firstname Lastname'}
              </Skeleton>
            }
            onClick={() =>
              push({
                pathname: routes.username(),
                query: { businessUnit: query.businessUnit },
              })
            }
          />
        </>
      )}
      {!(isLoading || userQuery.data?.isDsoLinkedStaff) && (
        <NavItem
          icon={<Lock />}
          title={<Trans id="Password" />}
          subtitle={
            <div style={{ display: 'flex' }}>
              <Trans
                id="Last updated on <1></1>"
                components={{
                  1: (
                    <>
                      <Skeleton active={isPending}>
                        {lastPasswordReset || 'Password Changed'}
                      </Skeleton>
                    </>
                  ),
                }}
              />
            </div>
          }
          onClick={() =>
            push({
              pathname: routes.password(),
              query: { businessUnit: query.businessUnit },
            })
          }
        />
      )}
      {!(isLoading || userQuery.data?.isDsoLinkedStaff) && (
        <NavItem
          icon={<Mfa />}
          title={<Trans id="2-step verification" />}
          status={
            isPending
              ? undefined
              : mfaQuery?.data?.isMfaEnabled
              ? 'enabled'
              : 'disabled'
          }
          subtitle={
            <Trans id="Add an extra layer of security when signing in to your account." />
          }
          onClick={() =>
            isPending
              ? undefined
              : mfaQuery?.data?.isMfaEnabled
              ? push({
                  pathname: routes.mfa(),
                  query: { businessUnit: query.businessUnit },
                })
              : push({
                  pathname: routes.setupMfa(),
                  query: { businessUnit: query.businessUnit },
                })
          }
        />
      )}
      {(businessUnit === 'invisalign' || businessUnit === 'tps') && (
        <>
          <Typography variant={'heading2'}>
            <Trans id="Contact" />
          </Typography>
          <NavItem
            canNavigate={
              !isLoading &&
              userQuery.data?.username !== userQuery.data?.contact_email
            }
            icon={<CircleIcon type={'email'} />}
            title={<Trans id="Email" />}
            subtitle={
              <Skeleton active={isPending}>
                {userQuery?.data?.contact_email || 'Firstname Lastname'}
              </Skeleton>
            }
            onClick={() =>
              !isLoading &&
              userQuery.data?.username !== userQuery.data?.contact_email
                ? push({
                    pathname: routes.contactEmail(),
                    query: { businessUnit: query.businessUnit },
                  })
                : undefined
            }
          />
        </>
      )}
    </BasePageTemplate>
  );
};

export default IndexPage;
